/**
 * 결함 목록 계층 트리 · 평탄화 — 순수 함수
 *
 * - buildDefectTree:      결함 배열 → Array → String → Module 계층 트리
 * - buildDefectTreeRows:  트리 → v-virtual-scroll 단일 평면 배열
 */
import { resolveDefectHierarchyFields } from './resolveDefectHierarchyFields';
import { defectBelongsToModule } from './defectModuleMatch';
import { appPerfMark, appPerfMeasure } from '@/utils/appBootstrapPerfLog.js';

/** siteDesign·solarModuleSeq 없을 때 Array/String 묶음 키 */
export const ORPHAN_HIERARCHY_KEY = '__NULL__';

function isOrphanHierarchyKey(key) {
  return key === ORPHAN_HIERARCHY_KEY;
}

// ═══════════════════════════════════════════════════════════
// View 모드 집계 — 결함이 있는 Module 수
// ═══════════════════════════════════════════════════════════

/**
 * 결함 목록에서 결함이 1개 이상 등록된 distinct Module 수 반환
 * @param {object[]} defects — 결함 객체 배열 (solarModuleSeq 필드 필요)
 * @returns {number} 결함이 있는 고유 Module 개수
 */
export function countDefectiveModules(defects) {
  if (!defects?.length) return 0;
  const seqs = new Set();
  for (const d of defects) {
    const seq = d?.solarModuleSeq;
    if (seq != null && seq !== '') {
      seqs.add(String(seq));
    }
  }
  return seqs.size;
}

/** Module bucket 집계 — solarModuleSeq 없는 결함(AI 등)도 1건으로 카운트 */
function countModuleDefectBucket(defects) {
  const bySeq = countDefectiveModules(defects);
  if (bySeq > 0) return bySeq;
  return defects?.length > 0 ? 1 : 0;
}

/**
 * 결함 배열을 Array → String → Module 3단계 계층 트리로 구성한다.
 *
 * @param {object} p
 * @param {object[]} p.defects — 필터링된 결함 목록 (solarModuleSeq, stringId, arrayId 등 포함)
 * @param {(d: object) => string} p.fastDefectLabel — 결함 → 라벨
 * @param {string} p.defectLabelFallback — 라벨 fallback
 * @returns {object[]} tree — [{ type:'array', seq, displayId, count, defects, children: [...] }]
 */
function mergeDisplayId(bucket, displayId) {
  if (bucket.displayId == null && displayId != null) {
    bucket.displayId = displayId;
  }
}

export function buildDefectTree({ defects, fastDefectLabel, defectLabelFallback }) {
  //━━━ 1단계: Module → String → Array 계층 Map 구축 ━━━
  // moduleKey = solarModuleSeq
  // stringKey = solarStringSeq (없으면 '__NULL__')
  // arrayKey  = solarArraySeq  (없으면 '__NULL__')

  /** @type {Map<string, { seq, displayId, defects: object[] }>} */
  const moduleMap = new Map();
  /** @type {Map<string, { seq, displayId, defects: object[], moduleKeys: Set<string> }>} */
  const stringMap = new Map();
  /** @type {Map<string, { seq, displayId, stringKeys: Set<string> }>} */
  const arrayMap = new Map();

  for (const defect of defects) {
    const hierarchy = resolveDefectHierarchyFields(defect);
    const rawModuleSeq = hierarchy.solarModuleSeq;
    const isOrphanModule = rawModuleSeq == null || rawModuleSeq === '';
    const moduleKey = isOrphanModule ? `__MOD_NO_SEQ_${defect.seq}` : String(rawModuleSeq);
    const rawStringSeq = hierarchy.solarStringSeq;
    const stringKey = rawStringSeq == null || rawStringSeq === '' ? ORPHAN_HIERARCHY_KEY : String(rawStringSeq);
    const rawArraySeq = hierarchy.solarArraySeq;
    const arrayKey = rawArraySeq == null || rawArraySeq === '' ? ORPHAN_HIERARCHY_KEY : String(rawArraySeq);

    // Module bucket
    if (!moduleMap.has(moduleKey)) {
      moduleMap.set(moduleKey, {
        seq: rawModuleSeq ?? null,
        displayId: hierarchy.moduleDisplayId ?? null,
        orphanModule: isOrphanModule,
        defects: []
      });
    }
    mergeDisplayId(moduleMap.get(moduleKey), hierarchy.moduleDisplayId);
    moduleMap.get(moduleKey).defects.push(defect);

    // String bucket
    if (!stringMap.has(stringKey)) {
      stringMap.set(stringKey, {
        seq: rawStringSeq ?? null,
        displayId: hierarchy.stringDisplayId,
        defects: [],
        moduleKeys: new Set()
      });
    }
    mergeDisplayId(stringMap.get(stringKey), hierarchy.stringDisplayId);
    stringMap.get(stringKey).moduleKeys.add(moduleKey);
    stringMap.get(stringKey).defects.push(defect);

    // Array bucket
    if (!arrayMap.has(arrayKey)) {
      arrayMap.set(arrayKey, {
        seq: rawArraySeq ?? null,
        displayId: hierarchy.arrayDisplayId,
        stringKeys: new Set()
      });
    }
    mergeDisplayId(arrayMap.get(arrayKey), hierarchy.arrayDisplayId);
    arrayMap.get(arrayKey).stringKeys.add(stringKey);
  }

  //━━━ 2단계: Array → String → Module 계층 빌드 ━━━
  const tree = [];

  for (const [arrayKey, arrayInfo] of arrayMap) {
    const arrayChildren = [];

    for (const stringKey of arrayInfo.stringKeys) {
      const stringInfo = stringMap.get(stringKey);
      if (!stringInfo) continue;

      const moduleChildren = [];
      for (const moduleKey of stringInfo.moduleKeys) {
        const modInfo = moduleMap.get(moduleKey);
        if (!modInfo) continue;
        moduleChildren.push({
          kind: 'module',
          seq: modInfo.seq,
          displayId: modInfo.displayId,
          orphanModule: !!modInfo.orphanModule,
          count: countModuleDefectBucket(modInfo.defects),
          defects: modInfo.defects,
          defectTypes: aggregateDefectTypes(modInfo.defects, fastDefectLabel, defectLabelFallback)
        });
      }

      arrayChildren.push({
        kind: 'string',
        seq: stringInfo.seq,
        displayId: stringInfo.displayId,
        orphanBucket: isOrphanHierarchyKey(stringKey),
        count: countModuleDefectBucket(stringInfo.defects),
        defects: stringInfo.defects,
        children: moduleChildren,
        defectTypes: aggregateDefectTypes(stringInfo.defects, fastDefectLabel, defectLabelFallback)
      });
    }

    // Array 내 String을 displayId 순으로 정렬
    arrayChildren.sort(sortByDisplayId);

    tree.push({
      kind: 'array',
      seq: arrayInfo.seq,
      displayId: arrayInfo.displayId,
      orphanBucket: isOrphanHierarchyKey(arrayKey),
      count: arrayChildren.reduce((sum, s) => sum + s.count, 0),
      children: arrayChildren
    });
  }

  // Array 정렬: 미지정 패널 맨 위 → 나머지 displayId 순
  tree.sort(sortArrayNodes);

  return tree;
}

/**
 * siteDesign 계층 + 결함 목록 → 모듈리스트 트리
 * array.seq = siteDesign array.id (PK)
 */
export function buildModuleTreeFromSiteDesign({
  hierarchy,
  defects,
  fastDefectLabel,
  defectLabelFallback
}) {
  if (!hierarchy?.length) return [];

  appPerfMark('defectTree:siteDesign:start', {
    arrayCount: hierarchy.length,
    defectCount: defects?.length ?? 0
  });

  const defectsByModule = new Map();
  const defectsByModuleDisplayId = new Map();
  for (const defect of defects || []) {
    const hierarchyFields = resolveDefectHierarchyFields(defect);
    const { solarModuleSeq, moduleDisplayId } = hierarchyFields;
    if (solarModuleSeq != null && solarModuleSeq !== '') {
      const key = String(solarModuleSeq);
      if (!defectsByModule.has(key)) defectsByModule.set(key, []);
      defectsByModule.get(key).push(defect);
    }

    if (moduleDisplayId != null && moduleDisplayId !== '') {
      const displayKey = String(moduleDisplayId);
      if (!defectsByModuleDisplayId.has(displayKey)) {
        defectsByModuleDisplayId.set(displayKey, []);
      }
      defectsByModuleDisplayId.get(displayKey).push(defect);
    }
  }

  let moduleCount = 0;
  const tree = hierarchy.map(arrayNode => {
    const arrayChildren = (arrayNode.strings || []).map(stringNode => {
      const moduleChildren = (stringNode.modules || []).map(mod => {
        moduleCount += 1;
        const moduleRef = {
          seq: mod.seq,
          displayId: mod.displayId,
          arrayDisplayId: arrayNode.displayId,
          stringDisplayId: stringNode.displayId
        };
        const bySeq = defectsByModule.get(String(mod.seq)) || [];
        const byDisplay = (defectsByModuleDisplayId.get(String(mod.displayId)) || []).filter(
          d => defectBelongsToModule(d, moduleRef) && !bySeq.includes(d)
        );
        const modDefects = [...bySeq, ...byDisplay];
        return {
          kind: 'module',
          seq: mod.seq,
          displayId: mod.displayId,
          count: countModuleDefectBucket(modDefects),
          defects: modDefects,
          defectTypes: aggregateDefectTypes(modDefects, fastDefectLabel, defectLabelFallback)
        };
      }).sort(sortByDisplayId);

      const stringDefects = moduleChildren.flatMap(m => m.defects);
      return {
        kind: 'string',
        seq: stringNode.seq,
        displayId: stringNode.displayId,
        count: countModuleDefectBucket(stringDefects),
        defects: stringDefects,
        children: moduleChildren,
        defectTypes: aggregateDefectTypes(stringDefects, fastDefectLabel, defectLabelFallback)
      };
    }).sort(sortByDisplayId);

    return {
      kind: 'array',
      seq: arrayNode.seq,
      displayId: arrayNode.displayId,
      count: arrayChildren.reduce((sum, s) => sum + s.count, 0),
      children: arrayChildren
    };
  });

  appPerfMeasure('defectTree:siteDesign:done', 'defectTree:siteDesign:start', {
    arrayCount: hierarchy.length,
    moduleCount,
    defectCount: defects?.length ?? 0
  });

  return tree;
}

function aggregateDefectTypes(defects, fastDefectLabel, fallback) {
  const typeCount = {};
  defects.forEach(d => {
    const label = fastDefectLabel(d);
    if (label && label !== fallback) {
      typeCount[label] = (typeCount[label] || 0) + 1;
    }
  });
  return Object.entries(typeCount).map(([name, count]) => ({
    name,
    count: count > 1 ? count : null
  }));
}

function sortByDisplayId(a, b) {
  const aid = a.displayId != null ? Number(a.displayId) : NaN;
  const bid = b.displayId != null ? Number(b.displayId) : NaN;
  if (!isNaN(aid) && !isNaN(bid)) return aid - bid;
  if (!isNaN(aid)) return -1;
  if (!isNaN(bid)) return 1;
  return String(a.displayId ?? '').localeCompare(String(b.displayId ?? ''));
}

/** Array 노드 정렬 — 미지정(orphan) 패널을 항상 맨 위 */
function sortArrayNodes(a, b) {
  if (!!a.orphanBucket !== !!b.orphanBucket) {
    return a.orphanBucket ? -1 : 1;
  }
  if (a.seq == null) return 1;
  if (b.seq == null) return -1;
  return sortByDisplayId(a, b);
}

/**
 * 트리 → v-virtual-scroll 단일 평면 배열
 *
 * expandedSeqs: 펼쳐진 노드의 kind+seq 를 Set<string> 으로 관리 (예: "array_3", "string_5", "module_42")
 *
 * @param {object} p
 * @param {object[]} p.tree          — buildDefectTree 결과
 * @param {Set<string>} p.expandedSeqs — 펼쳐진 노드 식별자 Set
 * @returns {object[]} flat rows
 */
export function buildDefectTreeRows({ tree, expandedSeqs }) {
  const expanded = expandedSeqs instanceof Set ? expandedSeqs : new Set();
  const rows = [];

  const pushModuleFlatRow = (arrayNode, stringNode, moduleNode) => {
    const defects = moduleNode.defects ?? [];
    const moduleBase = {
      seq: moduleNode.seq,
      displayId: moduleNode.displayId,
      orphanModule: !!moduleNode.orphanModule,
      arrayDisplayId: arrayNode.displayId,
      arraySeq: arrayNode.seq,
      stringDisplayId: stringNode.displayId,
      stringSeq: stringNode.seq,
      count: moduleNode.count,
      defectCount: defects.length,
      defects,
      defectTypes: moduleNode.defectTypes ?? []
    };

    if (moduleNode.count === 0 && defects.length === 0) {
      rows.push({
        kind: 'panel',
        ...moduleBase,
        defectCount: 0,
        defects: []
      });
    } else {
      rows.push({
        kind: 'panel_single',
        ...moduleBase,
        defect: defects[0] ?? null
      });
    }
  };

  for (const arrayNode of tree) {
    const arrayId = `array_${arrayNode.seq ?? '__'}`;
    rows.push({
      kind: 'array_header',
      seq: arrayNode.seq,
      displayId: arrayNode.displayId,
      orphanBucket: !!arrayNode.orphanBucket,
      count: arrayNode.count,
      expanded: expanded.has(arrayId),
      defectTypes: []
    });

    if (!expanded.has(arrayId)) continue;

    if (arrayNode.orphanBucket) {
      for (const stringNode of arrayNode.children) {
        for (const moduleNode of stringNode.children) {
          pushModuleFlatRow(arrayNode, stringNode, moduleNode);
        }
      }
      continue;
    }

    for (const stringNode of arrayNode.children) {
      const stringId = `string_${stringNode.seq ?? '__'}`;
      rows.push({
        kind: 'string_header',
        seq: stringNode.seq,
        displayId: stringNode.displayId,
        orphanBucket: !!stringNode.orphanBucket,
        arrayDisplayId: arrayNode.displayId,
        arraySeq: arrayNode.seq,
        count: stringNode.count,
        expanded: expanded.has(stringId),
        defectTypes: stringNode.defectTypes
      });

      if (!expanded.has(stringId)) continue;

      for (const moduleNode of stringNode.children) {
        pushModuleFlatRow(arrayNode, stringNode, moduleNode);
      }
    }
  }

  return rows;
}

/**
 * View 모드: 결함 없는 Module(count === 0)을 트리에서 제거
 * keepEmptyArrays: true 이면 Array 헤더는 결함 0이어도 유지 (모듈리스트용)
 */
export function filterZeroDefectModules(tree, { keepEmptyArrays = false } = {}) {
  if (!tree?.length) return [];

  return tree.reduce((filteredArrays, arrayNode) => {
    const arrayChildren = (arrayNode.children || []).reduce((filteredStrings, stringNode) => {
      const moduleChildren = (stringNode.children || []).filter(
        m => m.count > 0 || (m.defects?.length ?? 0) > 0
      );
      if (!moduleChildren.length) return filteredStrings;

      filteredStrings.push({
        ...stringNode,
        count: moduleChildren.reduce((sum, m) => sum + m.count, 0),
        children: moduleChildren
      });
      return filteredStrings;
    }, []);

    if (!arrayChildren.length) {
      if (keepEmptyArrays) {
        filteredArrays.push({
          ...arrayNode,
          count: 0,
          children: []
        });
      }
      return filteredArrays;
    }

    filteredArrays.push({
      ...arrayNode,
      count: arrayChildren.reduce((sum, s) => sum + s.count, 0),
      children: arrayChildren
    });
    return filteredArrays;
  }, []);
}

/** @deprecated — buildDefectTree / buildDefectTreeRows 사용 */
export { buildDefectTree as buildPanelGroups, buildDefectTreeRows as buildFlatRows };
